// client/src/hooks/usePlanCScoreUplift.ts
//
// TRIL-116: subscribes to reputation.updated WS events. When the caller
// arms the observer (called from form submit success), the next reputation
// event within 30s fires plan_c.score_uplift_observed. After 30s, the
// observer disarms automatically.

import * as React from 'react';
import { useSocket } from '../context/WebsocketContext';
import { trackEvent } from '../helpers/analytics';

const UPLIFT_WINDOW_MS = 30_000;

// WS payload shape matches server/src/helpers/reputation/urgScoring.ts —
// `delta` is a per-dimension score change map; `delta.overallScore` is the
// top-level uplift the analytics event reports as `score_delta`.
interface ReputationUpdatedPayload {
  delta: { overallScore: number; [k: string]: number };
  reputation: { confidence: number };
}

export function usePlanCScoreUplift(): { armUpliftObserver: () => void } {
  const { onEvent } = useSocket();
  const armedUntil = React.useRef<number>(0);
  const priorConfidence = React.useRef<number | null>(null);

  React.useEffect(() => {
    const unsubscribe = onEvent(
      'reputation.updated',
      (data: ReputationUpdatedPayload) => {
        if (Date.now() > armedUntil.current) return;
        armedUntil.current = 0;

        const confidence_delta =
          priorConfidence.current != null
            ? data.reputation.confidence - priorConfidence.current
            : 0;
        priorConfidence.current = data.reputation.confidence;

        trackEvent('plan_c.score_uplift_observed', {
          score_delta: data.delta.overallScore,
          confidence_delta,
        });
      },
    );
    return unsubscribe;
  }, [onEvent]);

  const armUpliftObserver = React.useCallback(() => {
    armedUntil.current = Date.now() + UPLIFT_WINDOW_MS;
  }, []);

  return { armUpliftObserver };
}
